import renderSiteWatermark from './site_watermark'

/**
 * GICS Breadth Chart implementation for Highcharts
 *
 * @param {HTMLElement} container - The DOM element to render the chart in
 * @param {Object} data - Chart data with breadth information
 * @param {Object} options - Additional chart options
 */
/**
 * Pick a readable label colour (near-black or white) for a given box colour.
 * Handles the two formats the server sends: hex ("#5D87FE") and
 * "hsl(h, s%, l%)" strings. Uses YIQ perceived brightness.
 *
 * Highcharts' `color: 'contrast'` only works for SVG labels; these data
 * labels are useHTML, so contrast must be computed here.
 */
export function contrastTextColor(color) {
  const rgb = parseColorToRgb(color)
  if (!rgb) return '#1A1A1A'
  const yiq = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000
  return yiq >= 145 ? '#1A1A1A' : '#FFFFFF'
}

function parseColorToRgb(color) {
  if (typeof color !== 'string') return null

  const hex = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)
  if (hex) {
    let value = hex[1]
    if (value.length === 3) value = value.replace(/./g, c => c + c)
    return [
      parseInt(value.slice(0, 2), 16),
      parseInt(value.slice(2, 4), 16),
      parseInt(value.slice(4, 6), 16)
    ]
  }

  const hsl = color.match(/^hsl\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%\s*\)$/i)
  if (hsl) {
    return hslToRgb(parseFloat(hsl[1]), parseFloat(hsl[2]) / 100, parseFloat(hsl[3]) / 100)
  }

  return null
}

function hslToRgb(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const hp = (h % 360) / 60
  const x = c * (1 - Math.abs((hp % 2) - 1))
  let r = 0, g = 0, b = 0
  if (hp < 1) [r, g, b] = [c, x, 0]
  else if (hp < 2) [r, g, b] = [x, c, 0]
  else if (hp < 3) [r, g, b] = [0, c, x]
  else if (hp < 4) [r, g, b] = [0, x, c]
  else if (hp < 5) [r, g, b] = [x, 0, c]
  else [r, g, b] = [c, 0, x]
  const m = l - c / 2
  return [Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255)]
}

// Fired on the chart container when a box is clicked. The theme-constituents
// Stimulus controller listens for it; kept here so the chart needs no knowledge
// of Stimulus and the two sides can't drift on the event name.
export const BREADTH_SELECT_EVENT = 'breadth:select'

export default function createGicsBreadthChart(container, data, options = {}) {
  // Theme names are short enough to sit horizontally in a box; GICS names
  // need the full box height, so they stay rotated -90.
  const horizontalLabels = options.level === 'theme'

  // Only the theme tab has a drill-down destination, and only for entitled
  // users (options.clickable comes from the server's tier check).
  const clickable = horizontalLabels && options.clickable === true

  // Group data by percentage bins (5% increments from 0-100%)
  const processedData = processGicsBreadthData(data)
  
  // Calculate max stacked height for the chart
  const maxStackHeight = Math.max(...processedData.bins.map(bin => bin.length * 8), 5)
  
  // Ensure we have data to show
  if (processedData.seriesData.length === 0) {
    console.error('No valid data points to display')
    container.innerHTML = 
      '<div class="alert alert-warning">No data available to display in the chart. Please try a different selection.</div>'
    return null
  }
  
  // Create the chart configuration
  const chartConfig = {
    chart: {
      type: 'column',
      zoomType: 'xy',
      backgroundColor: 'transparent',
      style: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial'
      },
      marginLeft: 0,
      marginRight: 0,
      spacingLeft: 0,
      spacingRight: 0,
      events: {
        // Site watermark, centred over the Strong (60-100%) zone — the top of
        // that band is effectively always clear of boxes.
        render: function() {
          renderSiteWatermark(this, (chart, box) => ({
            x: chart.xAxis[0].toPixels(80) - box.width / 2,
            y: chart.plotTop + box.height + 6
          }))
        }
      }
    },
    title: {
      text: null
    },
    xAxis: {
      plotBands: [
        { // Weak zone (0-40%)
          from: 0,
          to: 40,
          color: 'rgba(218, 104, 104, 0.15)',
          zIndex: -1,
          borderWidth: 1,
          borderColor: 'rgba(218, 104, 104, 0.3)'
        },
        { // Neutral zone (40-60%)
          from: 40,
          to: 60,
          color: 'rgba(238, 172, 93, 0.15)',
          zIndex: -1,
          borderWidth: 1,
          borderColor: 'rgba(238, 172, 93, 0.3)'
        },
        { // Strong zone (60-100%)
          from: 60,
          to: 100,
          color: 'rgba(104, 187, 138, 0.15)',
          zIndex: -1,
          borderWidth: 1,
          borderColor: 'rgba(104, 187, 138, 0.3)'
        }
      ],
      title: {
        text: 'Percentage of Stocks Above MA'
      },
      min: 0,
      max: 100,
      gridLineWidth: 1,
      gridLineColor: 'rgba(0, 0, 0, 0.1)',
      tickPositions: [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100],
      plotLines: [
        {
          value: 40,
          color: 'rgba(0, 0, 0, 0.3)',
          width: 2,
          zIndex: 3,
          dashStyle: 'Dash'
        },
        {
          value: 60,
          color: 'rgba(0, 0, 0, 0.3)',
          width: 2,
          zIndex: 3,
          dashStyle: 'Dash'
        }
      ],
      startOnTick: true,
      endOnTick: true
    },
    yAxis: {
      title: {
        text: null
      },
      labels: {
        enabled: false
      },
      min: 0,
      max: Math.max(20, maxStackHeight + 10),
      gridLineWidth: 0
    },
    legend: {
      enabled: false
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        grouping: false,
        pointPadding: 0,
        groupPadding: 0,
        borderWidth: 1,
        pointWidth: null,
        dataLabels: {
          enabled: true,
          // Rotated useHTML labels report unreliable bounding boxes in HC12, so
          // Highcharts' default overlap detection silently hides colliding labels.
          // Every stacked segment must keep its label, so opt out of hiding.
          allowOverlap: true,
          formatter: function() {
            const name = this.point.name || ''
            if (!name) return ''

            // Abbreviate known long words
            const abbreviations = {
              'Telecommunication': 'Telecom',
              'Semiconductors': 'Semicon.',
              'Semiconductor': 'Semicon.',
              'Transportation': 'Transport',
              'Pharmaceuticals': 'Pharma',
              'Biotechnology': 'Biotech',
              'Entertainment': 'Entertain.',
              'Professional': 'Prof.',
              'Discretionary': 'Discret.',
              'Distribution': 'Distrib.',
              'Development': 'Develop.',
              'Management': 'Mgmt',
              'Investment': 'Invest.',
              'Components': 'Compon.',
              'Automobiles': 'Auto',
              'Commercial': 'Commerc.',
              'Equipment': 'Equip.',
              'Infrastructure': 'Infrastr.',
              'Cybersecurity': 'Cybersec.'
            }

            let processedName = name
            for (const [long, short] of Object.entries(abbreviations)) {
              processedName = processedName.replace(new RegExp(long, 'g'), short)
            }

            // Greedily wrap words so no single rendered line is longer than
            // the space it renders across (~5.8px per character at 10px/600).
            // Horizontal labels wrap against the bin width (plotWidth / 20
            // bins), which shrinks with the viewport. Rotated labels wrap
            // against the box height (8 y-units of the axis, so it shrinks
            // as the fullest bin grows) — capped at the historical 13 so
            // roomy boxes keep today's layout. When the budget is tight,
            // hard-break any single word over it so nothing can bleed into
            // a neighbouring bin or box.
            const ROTATED_MAX_CHARS = 13
            const chart = this.series && this.series.chart
            let maxChars = ROTATED_MAX_CHARS
            let hardWrap = false
            if (horizontalLabels) {
              const plotWidth = (chart && chart.plotWidth) || 0
              maxChars = Math.max(6, Math.min(20, Math.floor(plotWidth / 20 / 5.8)))
              hardWrap = true
            } else if (chart && chart.plotHeight && chart.yAxis && chart.yAxis[0] && chart.yAxis[0].max) {
              const boxHeight = chart.plotHeight * 8 / chart.yAxis[0].max
              const budget = Math.floor(boxHeight / 5.8)
              if (budget < ROTATED_MAX_CHARS) {
                maxChars = Math.max(6, budget)
                hardWrap = true
              }
            }
            let words = processedName.split(' ')
            if (hardWrap) {
              words = words.flatMap(word =>
                word.length > maxChars ? word.match(new RegExp('.{1,' + maxChars + '}', 'g')) : [word]
              )
            }
            const lines = []
            let current = ''
            for (const word of words) {
              if (!current) {
                current = word
              } else if ((current + ' ' + word).length <= maxChars) {
                current += ' ' + word
              } else {
                lines.push(current)
                current = word
              }
            }
            if (current) lines.push(current)
            // useHTML labels ignore style.color 'contrast', so pick the
            // readable colour per point from its box colour.
            const textColor = contrastTextColor(this.point.color)
            return '<div style="text-align:center;color:' + textColor + '">' + lines.join('<br>') + '</div>'
          },
          style: {
            textOutline: 'none',
            fontWeight: '600',
            fontSize: '10px',
            textAlign: 'center'
          },
          rotation: horizontalLabels ? 0 : -90,
          verticalAlign: 'middle',
          align: 'center',
          inside: true,
          overflow: 'allow',
          crop: false,
          useHTML: true
        },
        states: {
          hover: {
            brightness: 0.1
          }
        },
        // Click-through to the theme's constituents. Only wired when the server
        // says the viewer is entitled (Trader+), so Free users get an inert box
        // rather than a modal that only tells them to upgrade — the banner above
        // the chart carries their upgrade prompt instead.
        cursor: clickable ? 'pointer' : undefined,
        point: clickable ? {
          events: {
            click: function() {
              container.dispatchEvent(new CustomEvent(BREADTH_SELECT_EVENT, {
                bubbles: true,
                detail: {
                  entityId: this.entityId,
                  name: this.name,
                  percentage: this.percentage,
                  matchCount: this.matchCount,
                  totalCount: this.totalCount
                }
              }))
            }
          }
        } : undefined
      }
    },
    tooltip: {
      useHTML: true,
      headerFormat: '<table>',
      footerFormat: '</table>',
      followPointer: true,
      outside: true,
      formatter: function() {
        // Calculate the correct percentage from the raw data
        var percentage = Math.round(this.point.matchCount / this.point.totalCount * 100)
        // RS is only present in the theme tab's "colour by RS" mode, and only
        // for users entitled to see it — the server omits the key otherwise.
        const rsRow = (this.point.rs === null || this.point.rs === undefined)
          ? ''
          : '<tr><td>Theme RS:</td><td><b>' + this.point.rs + '</b></td></tr>'

        return '<table>' +
               '<tr><th colspan="2"><h5>' + this.point.name + '</h5></th></tr>' +
               '<tr><td>Percentage Above MA:</td><td><b>' + percentage + '%</b></td></tr>' +
               '<tr><td>Stocks Above MA:</td><td>' + this.point.matchCount + ' of ' + this.point.totalCount + '</td></tr>' +
               rsRow +
               '</table>'
      }
    },
    series: [{
      name: 'GICS Groups',
      data: processedData.seriesData,
      turboThreshold: 1000,
      animation: false,
      colorByPoint: true,
      pointPlacement: 'on',
      stickyTracking: false,
      enableMouseTracking: true
    }],
    credits: {
      enabled: false
    },
    ...options
  }
  
  // Create the Highcharts chart
  return Highcharts.chart(container, chartConfig)
}

/**
 * Process GICS breadth data into bins and series data for the chart
 * 
 * @param {Array|Object} data - Raw GICS breadth data
 * @returns {Object} - Processed data with bins and series data
 */
function processGicsBreadthData(breadthData) {
  // Handle empty or invalid data
  if (!breadthData) {
    console.error('Invalid or empty data for GICS breadth chart')
    return { bins: [], seriesData: [] }
  }
  
  // If data is a string, try to parse it
  if (typeof breadthData === 'string') {
    try {
      breadthData = JSON.parse(breadthData)
    } catch (error) {
      console.error('Error parsing GICS breadth data:', error)
      return { bins: [], seriesData: [] }
    }
  }
  
  // If data is in raw format (from server), extract the array
  if (breadthData.breadth_data) {
    breadthData = breadthData.breadth_data
  }
  
  // Ensure we have an array to work with
  if (!Array.isArray(breadthData)) {
    console.error('Invalid GICS breadth data format')
    return { bins: [], seriesData: [] }
  }
  
  const numBins = 20 // 100% / 5% = 20 bins
  const binSize = 5 // 5% per bin
  
  // Create bins to hold groups
  const bins = Array(numBins).fill().map(() => [])
  
  // Group data into bins based on percentage
  breadthData.forEach(item => {
    if (!item || typeof item.percentage !== 'number') {
      console.warn('Invalid item in breadthData:', item)
      return
    }
    
    // Calculate bin (0-19)
    const bin = Math.floor(item.percentage / binSize)
    const binIndex = Math.min(bin, numBins - 1)
    
    // Grouping key for the within-bin sort: an explicit server-sent group_key
    // if present (the theme tab's RS colouring mode sends one so boxes cluster
    // by RS band rather than by category), else sector_code (GICS sector
    // prefix, or theme category). Deriving it from the id only suits GICS
    // codes, where the first two digits ARE the sector — a theme's integer DB
    // id would scatter same-category boxes — so it's a last-resort fallback.
    let sectorCode

    if (item.group_key) {
      sectorCode = item.group_key.toString()
    } else if (item.sector_code) {
      sectorCode = item.sector_code.toString()
    } else if (item.id) {
      sectorCode = item.id.toString().substring(0, 2)
    } else {
      sectorCode = ''
    }

    // Create color based on sector code
    let itemColor

    // Use color if directly provided
    if (item.color) {
      itemColor = item.color
    }
    // Otherwise generate one from sector code (numeric codes only — theme
    // categories are strings, but the server always sends them a color)
    else if (sectorCode && !isNaN(parseInt(sectorCode))) {
      // Generate consistent colors for each sector with improved saturation and lightness
      const hue = (parseInt(sectorCode) * 137.5) % 360
      itemColor = `hsl(${hue}, 65%, 55%)`
    } else {
      // Fallback color
      itemColor = '#A1A09E'
    }
    
    // Add to appropriate bin
    bins[binIndex].push({
      id: item.id,
      name: item.name || 'Unknown',
      percentage: item.percentage,
      match_count: item.match_count || 0,
      total_count: item.total_count || 0,
      color: itemColor || '#CCCCCC',
      sectorCode: sectorCode,
      rs: (item.rs === undefined ? null : item.rs)
    })
  })
  
  // Sort items within each bin by sector code 
  bins.forEach(bin => {
    bin.sort(function(a, b) {
      const sectorA = String(a.sectorCode || '')
      const sectorB = String(b.sectorCode || '')
      // Use simple string comparison
      return sectorA < sectorB ? -1 : sectorA > sectorB ? 1 : 0
    })
  })
  
  // Convert bins to column chart data points
  const seriesData = []
  
  bins.forEach((items, binIndex) => {
    // Calculate x position for this bin
    const xPos = binIndex * binSize // Left edge of the bin
    
    // Create a data point for each item in the bin
    items.forEach((item, itemIndex) => {
      // Ensure we have a valid color for visualization
      const useColor = item.color || '#CCCCCC'
      
      // For each item, create a column data point
      seriesData.push({
        x: xPos + (binSize / 2), // Center within the bin
        y: 8,                    // Increased height for each rectangle to fit labels
        // Entity id (theme id at the theme level, GICS code otherwise) — the
        // theme tab's click-through needs it to fetch that theme's constituents.
        entityId: item.id,
        name: item.name,
        color: useColor,
        percentage: item.percentage,
        matchCount: item.match_count,
        totalCount: item.total_count,
        // Add stack identifier to ensure proper stacking
        stack: 'bin-' + binIndex,
        // Add debug info
        sectorCode: item.sectorCode,
        rs: item.rs
      })
    })
  })
  
  return { bins, seriesData }
}